import {
  get,
  omitBy,
  isUndefined,
  map,
  head,
  capitalize,
  startsWith,
  isEmpty,
  split,
} from 'lodash';

import { MSRP_PRICE_TIER } from 'constants/pdp';
import OneTrustController from 'controllers/OneTrustController';
import Cloudinary from 'utils/Cloudinary';
import Customer from 'utils/Customer';
import DataDogLogs from 'utils/DataDogLogs';
import Environment from 'utils/Environment';
import Gender from 'utils/Gender';
import Log from 'utils/Log';
import { CURRENCIES } from 'utils/Money';
import Settings from 'utils/Settings';
import StorefrontUtil from 'utils/Storefront';

import {
  accountCreationCompleted,
  accountCreationStarted,
  callToActionClicked,
  cartUpsellSizeSelected,
  cartUpsellSwatchClicked,
  cartViewed,
  checkoutFailure,
  checkoutStarted,
  checkoutStepViewed,
  detailsAccordionOpened,
  experimentViewed,
  footerLinkClicked,
  giftMessageClicked,
  giftMessageSubmitted,
  globalBannerLinkClicked,
  homepageHeroClicked,
  homepageModuleClicked,
  miniNavigationClicked,
  navigationClicked,
  orderCompleted,
  pdpModuleClicked,
  productAdded,
  productClicked,
  productImageClicked,
  productImageScroll,
  productListFeatureClicked,
  productListFiltered,
  productListViewed,
  productRemoved,
  productsSearched,
  productsSearchInitiated,
  productViewed,
  recommendationsClicked,
  reviewsFiltered,
  reviewsLoadMore,
  reviewsSearched,
  reviewsSorted,
  reviewsStarsClicked,
  setRudderTyperOptions,
  sizeGuideOpened,
  userMarketingPreference,
} from './generated-client/rudderTyperClient';
import { loadRudderstackSdk } from './sdk-loader';

const Rudderstack = class {
  static init() {
    loadRudderstackSdk();
  }

  static _syncHasConsent() {
    return window?.OnetrustActiveGroups?.includes('C0002') || false;
  }

  static async _hasConsent() {
    const hasConsent = await new Promise(resolve => {
      OneTrustController.hasPerformanceConsent()
        .then(async () => {
          const isInitialized = await Rudderstack._waitIsRudderstackInitialized();
          if (isInitialized) {
            window.rudderanalytics.ready(() => {
              resolve(true);
            });
          } else {
            Log.error(new Error(`'window.rudderanalytics' object not found after consent`));
            DataDogLogs.error(`'window.rudderanalytics' object not found after consent`);
            resolve(false);
          }
        })
        .catch(() => {
          resolve(false);
        });
    });
    return hasConsent;
  }

  // we have this while rudderstack is initialized elsewhere in code
  // but events are still directly dispatched
  // remove when events are moved to mediator
  static async _waitIsRudderstackInitialized() {
    let count = 0;
    const maxRetryCount = 25; // 5 seconds of retry

    return new Promise(resolve => {
      const interval = setInterval(() => {
        if (window.rudderanalytics?.ready) {
          clearInterval(interval);
          resolve(true);
        } else {
          if (count >= maxRetryCount) {
            clearInterval(interval);
            resolve(false);
          }
          count++;
        }
      }, 200);
    });
  }

  static _formatPriceToFloat(price) {
    return Number((price / 100).toFixed(2));
  }

  static formatDate(date) {
    return date?.toISOString().split('T')[0];
  }

  static _getImageSrc(product) {
    const firstImageSrc = head(product.contentfulImages)?.src; // not how long we'll keep this might define new "featured image"
    if (!firstImageSrc) {
      return null;
    }
    const imageUrl = Cloudinary.buildAutoUploadUrl(firstImageSrc);
    let src = imageUrl;
    if (startsWith(src, '//')) {
      src = `https:${src}`;
    }

    return src;
  }

  static _getMasterNameFromFullName(fullName) {
    const masterName = fullName?.split(' - ')?.[0];
    return masterName;
  }

  // @param: Product
  static _formatMasterProduct(product, mergeProps) {
    const formattedProduct = {
      product_id: product.contentfulID,
      category: product.type,
      gender: Gender.getPluralGender(product.gender),
      handle: product.handle,
      material: capitalize(product.material),
      name: Rudderstack._getMasterNameFromFullName(product.fullName) || product.masterName,
      price: Rudderstack._formatPriceToFloat(product.price),
      silhouette: capitalize(product.silhouette),
      url: product.url,
      color_name: product.colorName,
      edition: capitalize(product.edition),
      hue: capitalize(head(product.hues)),
      image_url: Rudderstack._getImageSrc(product),
      on_sale: product.isSaleProduct,
      price_tier: product.priceTier,
      ...mergeProps,
    };

    return omitBy(formattedProduct, isUndefined);
  }

  // @param: Product
  // @param: Variant
  static _formatProductWithVariant(product, variant, mergeProps) {
    const id = get(variant, 'id');
    const variantGid = StorefrontUtil.convertToGid({ id, resourceType: 'ProductVariant' });
    const formattedProduct = {
      ...Rudderstack._formatMasterProduct(product),
      sku: get(variant, 'sku'),
      variant_id: variantGid,
      variant_title: get(variant, 'value'),
      variant_url: `/products/${product.handle}?size=${get(variant, 'handle')}`,
      ...mergeProps,
    };

    return omitBy(formattedProduct, isUndefined);
  }

  // @param: CartLineItem
  static _formatCartLineItem(lineItem, mergeProps) {
    const { product } = lineItem;
    const { variant } = lineItem;

    const formattedProduct = {
      quantity: lineItem.quantity,
      ...Rudderstack._formatProductWithVariant(product, variant),
      ...mergeProps,
    };

    return omitBy(formattedProduct, isUndefined);
  }

  // @param: CheckoutLineItemData
  static _formatCheckoutProduct(checkoutLineItemData, mergeProps) {
    const formattedProduct = {
      category: get(checkoutLineItemData, 'category'),
      color_name: get(checkoutLineItemData, 'color'),
      edition: capitalize(get(checkoutLineItemData, 'edition')),
      hue: capitalize(head(split(get(checkoutLineItemData, 'hues'), ','))),
      material: capitalize(get(checkoutLineItemData, 'material')),
      silhouette: capitalize(get(checkoutLineItemData, 'silhouette')),
      image_url: get(checkoutLineItemData, 'image'),
      name: Rudderstack._getMasterNameFromFullName(get(checkoutLineItemData, 'fullName')),
      gender: Gender.getPluralGender(get(checkoutLineItemData, 'gender')),
      price: Rudderstack._formatPriceToFloat(get(checkoutLineItemData, 'unformattedPrice')),
      price_tier: get(checkoutLineItemData, 'price_tier'),
      product_id: get(checkoutLineItemData, 'product_id'),
      quantity: get(checkoutLineItemData, 'quantity'),
      sku: get(checkoutLineItemData, 'sku'),
      handle: get(checkoutLineItemData, 'handle'),
      url: `/products/${get(checkoutLineItemData, 'handle')}`,
      variant_id: get(checkoutLineItemData, 'variant_id'),
      variant_title: get(checkoutLineItemData, 'size'),
      variant_url: `/products/${get(checkoutLineItemData, 'handle')}?size=${get(
        checkoutLineItemData,
        'size_handleized'
      )}`,
      on_sale: checkoutLineItemData.price_tier
        ? checkoutLineItemData.price_tier !== MSRP_PRICE_TIER
        : null,
      ...mergeProps,
    };

    return omitBy(formattedProduct, isUndefined);
  }

  static _generateCheckoutDataDecoration(discountCodes, checkoutLineItemData) {
    const currencyCode = Settings.currency;

    const formattedProducts = map(checkoutLineItemData, (lineItem, index) =>
      Rudderstack._formatCheckoutProduct(lineItem, { position: index })
    );

    return Object.freeze({
      coupon: isEmpty(discountCodes) ? undefined : discountCodes.toString(),
      checkout_url: window.location.href,
      currency: currencyCode,
      currency_symbol: get(CURRENCIES[currencyCode], 'units.symbol'),
      products: formattedProducts,
    });
  }

  static _convertToDecimal(value, defaultValue) {
    const result = parseFloat(value);

    return Number.isNaN(result) ? defaultValue : result;
  }

  static async page(category, name, properties, apiOptions, callback) {
    if (!(await Rudderstack._hasConsent())) return;

    window.rudderanalytics?.page(category, name, properties, apiOptions, callback);
  }

  static async viewProductDetails({ product, variant }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });
    const currencyCode = Settings.currency;

    const formattedProduct = variant
      ? Rudderstack._formatProductWithVariant(product, variant)
      : Rudderstack._formatMasterProduct(product);

    const payload = {
      ...formattedProduct,
      currency: currencyCode,
      currency_symbol: CURRENCIES[currencyCode].units.symbol,
    };

    productViewed(payload);
  }

  static async productListFeatureClicked({
    collection,
    featureCTA,
    featureLinkDestination,
    featureType,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const currencyCode = Settings.currency;

    const payload = {
      currency: currencyCode,
      currency_symbol: CURRENCIES[currencyCode].units.symbol,
      feature_link_destination: featureLinkDestination,
      feature_type: featureType,
      feature_cta: featureCTA,
      ...collection,
    };

    productListFeatureClicked(payload);
  }

  static async productListFiltered({ collection, activeFilters, collectionItems }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });
    const currencyCode = Settings.currency;

    const formattedProducts = map(collectionItems, (product, index) =>
      Rudderstack._formatMasterProduct(product, { position: index })
    );

    const cleanedFilters = activeFilters.map(filter => {
      return {
        type: filter.filterIdentifier,
        value: filter.option,
      };
    });

    const payload = {
      currency: currencyCode,
      currency_symbol: CURRENCIES[currencyCode].units.symbol,
      ...collection,
      filters: cleanedFilters,
      products: formattedProducts,
    };

    productListFiltered(payload);
  }

  static async productListViewed({ collection, collectionItems }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });
    const currencyCode = Settings.currency;

    const formattedProducts = map(collectionItems, (product, index) =>
      Rudderstack._formatMasterProduct(product, { position: index })
    );

    const payload = {
      currency: currencyCode,
      currency_symbol: CURRENCIES[currencyCode].units.symbol,
      ...collection,
      products: formattedProducts,
    };

    productListViewed(payload);
  }

  /**
   * This event was renamed on Amplitud as 'Product List Clicked'
   */
  static productClicked({ collection, collectionItem, position }) {
    /**
     * Ad Hoc Fix
     *  - Use sync consent validation when events fired on user interaction, replace 'await Rudderstack._hasConsent()'
     *  - productClicked() is being used after page is loaded, the SDKs should be loaded by then
     *
     */
    if (!Rudderstack._syncHasConsent()) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });
    const currencyCode = Settings.currency;

    const formattedProduct = Rudderstack._formatMasterProduct(collectionItem, { position });
    const payload = {
      currency: currencyCode,
      currency_symbol: CURRENCIES[currencyCode].units.symbol,
      ...collection,
      products: [formattedProduct],
    };

    productClicked(payload);
  }

  static async productAdded({
    cartLineItems,
    addedCartLineItem,
    addedCartLineItemQuantity,
    origination,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });
    const currencyCode = Settings.currency;

    const formattedProducts = map(cartLineItems, (lineItem, index) =>
      Rudderstack._formatCartLineItem(lineItem, { position: index })
    );

    const addedProduct = Rudderstack._formatCartLineItem(addedCartLineItem, {
      quantity: addedCartLineItemQuantity,
    });

    const payload = {
      ...addedProduct,
      products: formattedProducts,
      currency: currencyCode,
      currency_symbol: CURRENCIES[currencyCode].units.symbol,
      cart_added_trigger_source: origination,
    };

    productAdded(payload);
  }

  static async productRemoved({ cartLineItems, removedCartLineItem, removedCartLineItemQuantity }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });
    const currencyCode = Settings.currency;

    const formattedProducts = map(cartLineItems, (lineItem, index) =>
      Rudderstack._formatCartLineItem(lineItem, { position: index })
    );

    const removedProduct = Rudderstack._formatCartLineItem(removedCartLineItem, {
      quantity: removedCartLineItemQuantity,
    });

    const payload = {
      ...removedProduct,
      products: formattedProducts,
      currency: currencyCode,
      currency_symbol: CURRENCIES[currencyCode].units.symbol,
    };

    productRemoved(payload);
  }

  static async cartViewed({ cartLineItems }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });
    const currencyCode = Settings.currency;

    const formattedProducts = map(cartLineItems, (lineItem, index) =>
      Rudderstack._formatCartLineItem(lineItem, { position: index })
    );

    const payload = {
      products: formattedProducts,
      currency: currencyCode,
      currency_symbol: CURRENCIES[currencyCode].units.symbol,
    };

    cartViewed(payload);
  }

  static async checkoutStarted({
    checkoutId,
    checkoutLineItemData,
    discountAmount,
    discountCodes,
    shippingPrice,
    subtotalPrice,
    revenue,
    taxPrice,
    totalPrice,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const checkoutDataDecoration = Rudderstack._generateCheckoutDataDecoration(
      discountCodes,
      checkoutLineItemData
    );

    const payload = {
      ...checkoutDataDecoration,
      checkout_id: checkoutId,
      discount: discountAmount,
      revenue,
      shipping: shippingPrice,
      subtotal: subtotalPrice,
      tax: taxPrice,
      total: totalPrice,
      value: subtotalPrice,
    };

    checkoutStarted(payload);
  }

  static async checkoutStepViewed({
    checkoutId,
    stepParam,
    stepNameParam,
    checkoutLineItemData,
    discountAmount,
    discountCodes,
    totalPrice,
    subtotalPrice,
    shippingPrice,
    shippingMethod,
    taxPrice,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const checkoutDataDecoration = Rudderstack._generateCheckoutDataDecoration(
      discountCodes,
      checkoutLineItemData
    );

    const payload = {
      ...checkoutDataDecoration,
      checkout_id: checkoutId,
      discount: discountAmount,
      step: stepParam,
      step_name: stepNameParam,
      shipping: shippingPrice,
      shipping_method: shippingMethod,
      subtotal: subtotalPrice,
      tax: taxPrice,
      total: totalPrice,
    };

    checkoutStepViewed(payload);
  }

  static async checkoutFailureEvent({
    checkoutId,
    failureReason,
    checkoutLineItemData,
    shippingMethod,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const defaultCheckoutData = Rudderstack._generateCheckoutDataDecoration(
      undefined,
      checkoutLineItemData
    );

    const payload = {
      ...defaultCheckoutData,
      checkout_id: checkoutId,
      failure_reason: failureReason,
      shipping_method: shippingMethod,
    };

    checkoutFailure(payload);
  }

  static async outOfStockEvent(checkoutIdParam, checkoutLineItemData, shippingMethod) {
    if (!(await Rudderstack._hasConsent())) return;

    Rudderstack.checkoutFailureEvent({
      checkoutId: checkoutIdParam,
      failureReason: 'stock_problems',
      checkoutLineItemData,
      shippingMethod,
    });
  }

  /**
   *
   * Warning! - This method is no longer used, now the code is on a Custom Pixel on Checkout Extension
   *
   */
  static async orderCompleted({
    checkoutId,
    checkoutLineItemData,
    campaignId,
    templateId,
    discountAmount,
    discountCodes,
    shippingPrice,
    shippingMethod,
    subtotalPrice,
    revenue,
    taxPrice,
    totalPrice,
    orderId,
    orderName,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const checkoutDataDecoration = Rudderstack._generateCheckoutDataDecoration(
      discountCodes,
      checkoutLineItemData
    );

    const payload = {
      ...checkoutDataDecoration,
      campaignId, // NOTE: kept camel case to not affect downstream
      checkout_id: checkoutId,
      discount: discountAmount,
      order_id: orderId,
      order_name: orderName,
      revenue,
      shipping: shippingPrice,
      shipping_method: shippingMethod,
      subtotal: subtotalPrice,
      tax: taxPrice,
      templateId, // NOTE: kept camel case to not affect downstream
      total: totalPrice,
    };

    await Rudderstack.identifyUserEphemeralTraits({
      totalNumberOfPurchasesIncrement: 1,
      firstPurchaseDate: new Date(),
      totalSpent: totalPrice,
      firstProduct: checkoutDataDecoration?.products?.[0],
    });

    orderCompleted(payload);
  }

  static async experimentViewed({ analyticsData, experimentType }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      campaign_id: analyticsData?.campaignId ? analyticsData?.campaignId.toString() : undefined,
      campaign_name: analyticsData?.campaignName,
      experience_id: analyticsData?.experienceId
        ? analyticsData?.experienceId.toString()
        : undefined,
      experience_name: analyticsData?.experienceName,
      variation_id: analyticsData?.variationId ? analyticsData?.variationId.toString() : undefined,
      variation_name: analyticsData?.variationName,
      experiment_type: experimentType,
      web_platform: 'liquid',
    };

    experimentViewed(payload);
  }

  static async footerLinkClicked({ footerLinkCategory, footerLinkName }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      footer_link_category: footerLinkCategory,
      footer_link_name: footerLinkName,
    };

    footerLinkClicked(payload);
  }

  static async navigationClicked({
    navigationType,
    navigationLevel1,
    navigationLevel2,
    navigationLevel3,
    navigationSource,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      navigation_type: navigationType,
      navigation_level_1: navigationLevel1,
      navigation_level_2: navigationLevel2,
      navigation_level_3: navigationLevel3,
      navigation_source: navigationSource,
    };

    navigationClicked(payload);
  }

  static async productsSearched({ query, searchResultsCount, searchSource, searchEntry }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      query,
      number_of_search_results: searchResultsCount,
      search_source: searchSource,
      search_entry: searchEntry,
    };

    productsSearched(payload);
  }

  static async productsSearchInitiated({ searchSource, searchEntry }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      search_source: searchSource,
      search_entry: searchEntry,
    };

    productsSearchInitiated(payload);
  }

  static defaultMarketingPreferencesPayload() {
    return {
      messageMedium: 'Email',
      messageType: 'All',
      placement: null,
      optInStatus: false,
      cmsProductId: null,
      sku: null,
      purpose: null,
    };
  }

  static async userMarketingPreference({
    messageMedium,
    messageType,
    placement,
    purpose,
    optInStatus,
    cmsProductId,
    sku,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      message_medium: messageMedium,
      message_type: messageType,
      placement,
      purpose,
      opt_in_status: optInStatus,
      cms_product_id: cmsProductId,
      sku,
    };

    userMarketingPreference(payload);
  }

  static async identifyUser({ email, additionalTraits, apiOptions }) {
    if (!(await Rudderstack._hasConsent())) return;

    const proposedUserTraits = Customer.getUserTraits({ email, additionalTraits });
    const existingUserTraits = window.rudderanalytics.getUserTraits();
    const shouldUpdateUser = Customer.shouldUpdateUser(
      existingUserTraits,
      proposedUserTraits,
      email
    );

    if (!shouldUpdateUser) return;

    const userId = proposedUserTraits?.hashed_email || window?.rudderanalytics?.getUserId() || '';

    window.rudderanalytics.identify(userId, proposedUserTraits, apiOptions);
  }

  /**
   *
   * Warning! - This method is no longer used, now the code is on a Custom Pixel on Checkout Extension
   *
   */
  static async identifyUserEphemeralTraits({
    totalNumberOfPurchasesIncrement = undefined,
    firstPurchaseDate = undefined,
    totalSpent = undefined,
    firstProduct = undefined,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    const apiOptions = omitBy(
      {
        first_purchase_date: Rudderstack.formatDate(firstPurchaseDate),
        total_number_of_purchases: totalNumberOfPurchasesIncrement,
        total_spent: totalSpent,
        first_purchased_product: firstProduct?.name,
        inferred_gender: firstProduct?.gender,
      },
      isUndefined
    );

    const userId = window?.rudderanalytics?.getUserId() || '';

    if (isEmpty(apiOptions)) return;

    window.rudderanalytics.identify(userId, {}, { traits: apiOptions });
  }

  static async clearUser() {
    if (!(await Rudderstack._hasConsent())) return;

    window.rudderanalytics.reset();
  }

  static async globalBannerClicked({ message }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      global_banner_name: message,
      global_header_source: window.location.href,
    };

    globalBannerLinkClicked(payload);
  }

  /**
   * Helper method to wrap an analytic call that require a fowllow up navigation applying a delay
   *
   * There is an issue on Safari and Apple Iphone (webkit) when an event is fired right just before a navigation occures,
   * the browser is cancelling the javascript exec and not sending the event.
   *
   * As a workaround, we are delaying the navigation by 1 second
   *
   * This wrapper should be used on use cases where it is needed to send an event before navigating to a new URL
   *
   * Default delay is 900 MS
   */
  static navigationDelayForIphoneAndSafari({ analyticsFn, urlToNavigate, delayMs = 900 }) {
    DataDogLogs.warn('Rudderstack - navigation delay on iphone or safari', {
      isSafari: Environment.isSafariDesktop,
      isIphone: Environment.isAppleIphone,
    });

    // Call the analytics function
    analyticsFn();

    // Delay the navigation for iphone or safari
    if (Environment.isSafariDesktop || Environment.isAppleIphone) {
      setTimeout(() => {
        window.location.href = urlToNavigate;
      }, delayMs);

      // Inmediately navigate to the URL for other browsers
    } else {
      window.location.href = urlToNavigate;
    }
  }

  static homepageHeroClick({ heroImageName, linkCTAName, linkDestination }) {
    /**
     * Ad Hoc Fix
     *  - Use sync consent validation when events fired on user interaction, replace 'await Rudderstack._hasConsent()'
     *  - homepageHeroClick() is being used after page is loaded, the SDKs should be loaded by then
     *
     */
    if (!Rudderstack._syncHasConsent()) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      image_name: heroImageName,
      link_CTA_name: linkCTAName,
      link_destination: linkDestination,
    };

    homepageHeroClicked(payload);
  }

  static async homepageModuleClick({
    moduleType,
    moduleName,
    modulePosition,
    carouselCategoryName,
    carouselCardPosition,
    carouselCardName,
    imageName,
    linkCTAName,
    linkDestination,
  }) {
    /**
     * Ad Hoc Fix
     *  - Use sync consent validation when events fired on user interaction, replace 'await Rudderstack._hasConsent()'
     *  - homepageModuleClick() is being used after page is loaded, the SDKs should be loaded by then
     *
     */
    if (!Rudderstack._syncHasConsent()) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      module_type: moduleType,
      module_name: moduleName,
      module_position: modulePosition,
      carousel_category_name: carouselCategoryName,
      carousel_card_position: carouselCardPosition,
      carousel_card_name: carouselCardName,
      image_name: imageName,
      link_CTA_name: linkCTAName,
      link_destination: linkDestination,
    };

    homepageModuleClicked(payload);
  }

  static async productImageClick({ index, product }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      category: product.type,
      gender: Gender.getPluralGender(product.gender),
      handle: product.handle,
      name: Rudderstack._getMasterNameFromFullName(product.fullName) || product.masterName,
      product_id: product.contentfulID,
    };

    const payload = {
      pdp_image_position: index,
      products: [formattedProduct],
    };

    productImageClicked(payload);
  }

  static async productImageScroll({ index, product }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      gender: Gender.getPluralGender(product.gender),
      name: Rudderstack._getMasterNameFromFullName(product.fullName) || product.masterName,
      category: product.type,
    };

    const payload = {
      pdp_image_position: index,
      products: [formattedProduct],
    };

    productImageScroll(payload);
  }

  static async detailsAccordionOpened({ accordionOpened, gender, category, fullName, masterName }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      gender: Gender.getPluralGender(gender),
      name: Rudderstack._getMasterNameFromFullName(fullName) || masterName,
      category,
    };

    const payload = {
      accordion_opened: accordionOpened,
      products: [formattedProduct],
    };

    detailsAccordionOpened(payload);
  }

  static async sizeGuideOpened({ gender, category, fullName, masterName }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      gender: Gender.getPluralGender(gender),
      name: Rudderstack._getMasterNameFromFullName(fullName) || masterName,
      category,
    };

    const payload = {
      products: [formattedProduct],
    };

    sizeGuideOpened(payload);
  }

  static async accountCreationStarted() {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      sign_up_source: document.referrer,
    };

    accountCreationStarted(payload);
  }

  static async accountCreationCompleted() {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });
    const accountCreationDate = new Date();

    const payload = {
      sign_up_source: document.referrer,
      account_creation_date: Rudderstack.formatDate(accountCreationDate),
    };

    accountCreationCompleted(payload);
  }

  static async cartUpsellSwatchClicked({ category, name, colorName }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      category,
      color_name: colorName,
      gender: Gender.getGenderFromProductName(name),
      name,
    };

    cartUpsellSwatchClicked({ products: [formattedProduct] });
  }

  static async cartUpsellSizeSelectorClicked({ category, name, sku, colorName, isAvailable }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      category,
      color_name: colorName,
      gender: Gender.getGenderFromProductName(name),
      name,
      sku,
    };

    const payload = {
      products: [formattedProduct],
      in_stock: isAvailable,
    };

    cartUpsellSizeSelected(payload);
  }

  static async reviewsStarsClicked({ category, gender, fullName, masterName, reviewRating }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      gender: Gender.getPluralGender(gender),
      name: Rudderstack._getMasterNameFromFullName(fullName) || masterName,
      category,
      review_rating: reviewRating,
    };

    const payload = {
      products: [formattedProduct],
    };

    reviewsStarsClicked(payload);
  }

  static async reviewsFiltered({
    category,
    gender,
    fullName,
    masterName,
    reviewRating,
    filtersApplied,
    starRating,
    typicalSize,
    typicalWidth,
    sizePurchased,
    overallFit,
    activityLevel,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      gender: Gender.getPluralGender(gender),
      name: Rudderstack._getMasterNameFromFullName(fullName) || masterName,
      category,
      review_rating: reviewRating,
    };

    const payload = {
      search_filter_star_rating: starRating,
      search_filter_typical_size: typicalSize,
      search_filter_typical_width: typicalWidth,
      search_filter_size_purchased: sizePurchased,
      search_filter_overall_fit: overallFit,
      search_filter_activity_level: activityLevel,
      filters_applied: filtersApplied,
      products: [formattedProduct],
    };

    reviewsFiltered(payload);
  }

  static async reviewsSearched({
    category,
    gender,
    fullName,
    masterName,
    reviewRating,
    searchKeyword,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      gender: Gender.getPluralGender(gender),
      name: Rudderstack._getMasterNameFromFullName(fullName) || masterName,
      category,
      review_rating: reviewRating,
    };

    const payload = {
      search_keyword: searchKeyword,
      products: [formattedProduct],
    };

    reviewsSearched(payload);
  }

  static async reviewsSorted({ category, gender, fullName, masterName, reviewRating, searchSort }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      gender: Gender.getPluralGender(gender),
      name: Rudderstack._getMasterNameFromFullName(fullName) || masterName,
      category,
      review_rating: reviewRating,
    };

    const payload = {
      search_sort: searchSort,
      products: [formattedProduct],
    };

    reviewsSorted(payload);
  }

  static async reviewsLoadMore({ category, gender, fullName, masterName, reviewRating }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      gender: Gender.getPluralGender(gender),
      name: Rudderstack._getMasterNameFromFullName(fullName) || masterName,
      category,
      review_rating: reviewRating,
    };

    const payload = {
      products: [formattedProduct],
    };

    reviewsLoadMore(payload);
  }

  static async giftMessageClicked() {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    giftMessageClicked();
  }

  static async giftMessageSubmitted(isLogoFree) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    giftMessageSubmitted({ logo_free: isLogoFree });
  }

  static async recommendationsClicked({
    category,
    name,
    gender,
    handle,
    recommendationHeading,
    carouselCardPosition,
    campaignId,
    campaignName,
    variationId,
    variationName,
    experienceId,
    experienceName,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const formattedProduct = {
      gender: Gender.getPluralGender(gender),
      name,
      category,
      handle,
    };

    const payload = {
      products: [formattedProduct],
      recommendation_heading: recommendationHeading,
      carousel_card_position: carouselCardPosition,
      campaign_id: campaignId,
      campaign_name: campaignName,
      variation_id: variationId,
      variation_name: variationName,
      experience_id: experienceId,
      experience_name: experienceName,
      source_url: window.location.href,
    };

    recommendationsClicked(payload);
  }

  static async miniNavigationClicked({ linkCTAName, linkDestination }) {
    /**
     * Ad Hoc Fix
     *  - Use sync consent validation when events fired on user interaction, replace 'await Rudderstack._hasConsent()'
     *  - miniNavigationClicked() is being used after page is loaded, the SDKs should be loaded by then
     *
     */
    if (!Rudderstack._syncHasConsent()) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      link_CTA_name: linkCTAName,
      link_destination: linkDestination,
    };

    miniNavigationClicked(payload);
  }

  static async callToActionClicked({
    location,
    name,
    variationName,
    variationId,
    value,
    url,
    type,
    experienceName,
    copy,
    campaignName,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      location,
      variation_name: variationName,
      variation_id: variationId,
      value,
      url,
      type,
      name,
      experience_name: experienceName,
      copy,
      campaign_name: campaignName,
    };

    callToActionClicked(payload);
  }

  static async pdpModuleClicked({
    cardName,
    cardPosition,
    moduleType,
    moduleName,
    modulePosition,
    imageName,
    linkCTAName,
    linkDestination,
  }) {
    if (!(await Rudderstack._hasConsent())) return;

    setRudderTyperOptions({
      analytics: window.rudderanalytics,
    });

    const payload = {
      card_name: cardName,
      card_position: cardPosition,
      module_type: moduleType,
      module_name: moduleName,
      module_position: modulePosition,
      image_name: imageName,
      link_CTA_name: linkCTAName,
      link_destination: linkDestination,
    };

    pdpModuleClicked(payload);
  }

  static getMarketingQueryParams() {
    const params = new URLSearchParams(window.location.search);
    return {
      gclid: params.get('gclid'),
      msclkid: params.get('msclkid'),
      fbclid: params.get('fbclid'),
    };
  }
};

export default Rudderstack;
